// Pestaña «Back orders»: lo que quedó sin servir de las líneas que salieron en
// parcial, agrupado por cliente y con el más antiguo primero dentro de cada uno.
//
// Por defecto los ABIERTOS y los PREVISTOS (marcados «parcial» con la carga aún
// en el almacén: el pendiente es estimado y se fija al salir); el filtro de la
// columna «Estado» enseña también los reasignados y los cancelados. Cancelar
// pide un motivo obligatorio: un back order que desaparece sin explicación es
// justo lo que esta pestaña existe para evitar.
//
// Los filtros cuelgan de las cabeceras, como en la tabla de cargas, con su
// catálogo en cascada y la franja de chips encima. NO van a la URL: el efecto
// de URL de la página reescribe la query entera y los borraría.
//
// La antigüedad se cuenta en días de calendario desde `fechaBo` (`antiguedadDias`).
import { Fragment, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BarraFiltrosActivos, filtrosDe, type FiltroActivo } from '@/components/common/BarraFiltrosActivos'
import { CabeceraFiltrable } from '@/components/common/CabeceraFiltrable'
import { DialogoModal } from '@/components/common/DialogoModal'
import { EmptyState } from '@/components/common/EmptyState'
import { Loader } from '@/components/common/Loader'
import { useCancelarBackOrder, useBackOrders } from '@/hooks/useCargasSalida'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'
import type { BackOrder, EstadoBackOrder } from '@/types'
import { subtituloArticulo } from '@/utils/cargasSalida'
import {
  antiguedadDias, errorApiDe, FILTROS_BO_INICIALES, ordenarBackOrders, pasaFiltroBackOrder,
  type CampoFiltroBo, type ErrorApi, type FiltrosBackOrders,
} from '@/utils/fichaCarga'
import { opcionesConHueco, SIN_INFORMAR } from '@/utils/filtroHuecos'
import { filtrosBackOrders } from './filtrosBackOrders'
import { fmtDate, fmtQty } from './formatoSalida'

interface Props { dark: boolean }

const ESTADOS_BO: EstadoBackOrder[] = ['abierto', 'previsto', 'reasignado', 'cancelado']

/** Chip de estado del BO. Ámbar abierto (pendiente de resolver), azul
 *  reasignado (ya tiene destino), gris cancelado. */
const CHIP_BO: Record<EstadoBackOrder, { dark: string; light: string }> = {
  abierto:    { dark: 'bg-amber-600 text-white', light: 'bg-amber-600 text-white' },
  previsto:   { dark: 'ring-1 ring-amber-400/60 text-amber-300', light: 'ring-1 ring-amber-500 text-amber-700 bg-amber-50' },
  reasignado: { dark: 'bg-blue-600 text-white',  light: 'bg-blue-600 text-white' },
  cancelado:  { dark: 'bg-slate-600 text-white', light: 'bg-slate-600 text-white' },
}

/** Valores distintos y ordenados, como opciones de una lista. */
function opcionesDe(valores: string[]) {
  return [...new Set(valores.filter(Boolean))]
    .sort((a, b) => a.localeCompare(b))
    .map(valor => ({ valor, titulo: valor }))
}

export function BackOrdersTabla({ dark }: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const { backOrders, isLoading, isError, refetch } = useBackOrders()
  const cancelar = useCancelarBackOrder()
  const [filtros, setFiltros] = useState<FiltrosBackOrders>(FILTROS_BO_INICIALES)
  const [aCancelar, setACancelar] = useState<BackOrder | null>(null)
  const [motivo, setMotivo] = useState('')
  const [error, setError] = useState<ErrorApi | null>(null)

  const hoy = new Date()
  const sinCliente = t('cargasSalida.backOrders.sinCliente')
  const col = (k: string) => t(`cargasSalida.backOrders.columnas.${k}`)

  const filas = useMemo(
    () => ordenarBackOrders(backOrders.filter(b => pasaFiltroBackOrder(b, filtros))),
    [backOrders, filtros],
  )

  // Cada catálogo sale de las filas que pasan todos los DEMÁS filtros.
  const cabeceras = useMemo(() => {
    const patch = (p: Partial<FiltrosBackOrders>) => setFiltros(f => ({ ...f, ...p }))
    const en = (campo: CampoFiltroBo) => backOrders.filter(b => pasaFiltroBackOrder(b, filtros, campo))
    const estados = new Set(en('estado').map(b => b.estadoBo))
    return filtrosBackOrders(filtros, patch, {
      cliente: opcionesConHueco(en('cliente').map(b => b.clienteNombre), sinCliente),
      pedido: opcionesDe(en('pedido').map(b => b.pedido)),
      articulo: opcionesDe(en('articulo').map(b => b.articulo)),
      carga: opcionesDe(en('carga').map(b => b.carga)),
      estado: ESTADOS_BO
        .filter(e => estados.has(e) || filtros.estado.includes(e))
        .map(e => ({ valor: e, titulo: t(`cargasSalida.backOrders.estado.${e}`) })),
    }, t)
  }, [backOrders, filtros, sinCliente, t])

  const patch = (p: Partial<FiltrosBackOrders>) => setFiltros(f => ({ ...f, ...p }))
  const hayRango = !!(filtros.desde || filtros.hasta)
  const activos: FiltroActivo[] = [
    ...filtrosDe(filtros.pedido, pedido => patch({ pedido }), { campo: col('pedido'), prefijo: 'p:' }),
    ...filtrosDe(filtros.cliente, cliente => patch({ cliente }), {
      campo: t('cargasSalida.filtros.cliente'),
      prefijo: 'cl:',
      etiquetaDe: v => (v === SIN_INFORMAR ? sinCliente : v),
    }),
    ...filtrosDe(filtros.articulo, articulo => patch({ articulo }), { campo: col('articulo'), prefijo: 'a:' }),
    ...filtrosDe(filtros.carga, carga => patch({ carga }), { campo: col('carga'), prefijo: 'c:' }),
    ...filtrosDe(filtros.estado, v => patch({ estado: v as EstadoBackOrder[] }), {
      campo: col('estado'),
      prefijo: 'e:',
      etiquetaDe: v => t(`cargasSalida.backOrders.estado.${v}`),
    }),
    ...(hayRango ? [{
      clave: 'fecha',
      campo: t('cargasSalida.backOrders.fechaBo'),
      valor: `${filtros.desde ? fmtDate(filtros.desde) : '…'} – ${filtros.hasta ? fmtDate(filtros.hasta) : '…'}`,
      quitar: () => patch({ desde: '', hasta: '' }),
    }] : []),
  ]

  function rotulo(clave: string, etiqueta: string) {
    const filtro = cabeceras[clave]
    return filtro ? <CabeceraFiltrable dark={dark} etiqueta={etiqueta} filtro={filtro} /> : etiqueta
  }

  const cardCls = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200'
  // Cabecera fija dentro del contenedor de alto tope, como en la tabla de
  // cargas: los filtros viven en ella y tienen que seguir a mano al bajar.
  const th = `sticky top-0 z-20 px-2 py-2 text-left text-[10px] font-bold uppercase tracking-wider whitespace-nowrap border-b ${
    dark ? 'bg-slate-900 border-white/8 text-slate-500' : 'bg-white border-slate-200 text-slate-600'
  }`
  const td = `px-2 py-1.5 text-[11px] align-middle ${dark ? 'text-slate-300' : 'text-slate-700'}`

  const msgError = (e: ErrorApi) =>
    t(`cargasSalida.backOrders.errores.${e.codigo}`, {
      defaultValue: t('cargasSalida.backOrders.errores.error_inesperado'),
      ...(e.valores ?? {}),
    })

  function cerrarDialogo() {
    setACancelar(null)
    setMotivo('')
    setError(null)
  }

  function confirmarCancelacion() {
    if (!aCancelar || !motivo.trim()) return
    setError(null)
    cancelar.mutate(
      { id: aCancelar.id, motivo: motivo.trim() },
      { onSuccess: cerrarDialogo, onError: e => setError(errorApiDe(e)) },
    )
  }

  if (isLoading) {
    return (
      <div className={`rounded-xl border py-16 ${cardCls}`}>
        <Loader label={t('cargasSalida.backOrders.cargando')} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className={`rounded-xl border ${cardCls}`}>
        <EmptyState
          dark={dark}
          title={t('common.error')}
          description={t('cargasSalida.backOrders.error')}
          action={(
            <button type="button" onClick={() => refetch()} className="text-[12px] font-semibold text-sky-600 hover:text-sky-500 cursor-pointer hover:underline">
              {t('common.refresh')}
            </button>
          )}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3 min-h-[22px]">
        <BarraFiltrosActivos
          dark={dark}
          filtros={activos}
          onLimpiar={() => setFiltros({ ...FILTROS_BO_INICIALES, estado: [] })}
        />
        <span className={`shrink-0 text-[11px] ${dark ? 'text-slate-400' : 'text-slate-600'}`}>
          {t('cargasSalida.backOrders.resumen', { count: filas.length, total: backOrders.length })}
        </span>
      </div>

      <div className={`rounded-xl border overflow-auto scroll-thin max-h-[calc(100vh-18rem)] ${cardCls}`}>
        <table className="w-full border-collapse text-left min-w-[980px]">
          <thead>
            <tr>
              <th className={th}>{rotulo('pedido', col('pedido'))}</th>
              <th className={th}>{rotulo('articulo', col('articulo'))}</th>
              <th className={th}>{rotulo('carga', col('carga'))}</th>
              <th className={`${th} text-right`}>{col('pendiente')}</th>
              <th className={`${th} text-right`}>{rotulo('antiguedad', col('antiguedad'))}</th>
              <th className={th}>{rotulo('estado', col('estado'))}</th>
              <th className={th} />
            </tr>
          </thead>
          <tbody>
            {filas.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    dark={dark}
                    title={t(activos.length > 0 && backOrders.length > 0
                      ? 'cargasSalida.backOrders.vacioFiltrosTitulo'
                      : 'cargasSalida.backOrders.vacioTitulo')}
                    description={t('cargasSalida.backOrders.vacioDescripcion')}
                  />
                </td>
              </tr>
            )}
            {filas.map((b, i) => {
              const nuevoCliente = i === 0 || (filas[i - 1].clienteNombre ?? '') !== (b.clienteNombre ?? '')
              const dias = antiguedadDias(b.fechaBo, hoy)
              return (
                <Fragment key={b.id}>
                  {nuevoCliente && (
                    <tr className={dark ? 'bg-white/[0.04]' : 'bg-slate-100'}>
                      <td colSpan={7} className={`px-2 py-1.5 text-[11px] font-bold ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
                        {b.clienteNombre || sinCliente}
                      </td>
                    </tr>
                  )}
                  <tr className={`border-t ${dark ? 'border-white/6' : 'border-slate-100'}`}>
                    <td className={`${td} font-mono`}>{b.pedido}</td>
                    <td className={td}>
                      <span title={subtituloArticulo(fmtArt(b.articulo), b.color, b.estilo)}>
                        {subtituloArticulo(fmtArt(b.articulo), b.color, b.estilo)}
                      </span>
                    </td>
                    <td className={`${td} font-mono`}>{b.carga}</td>
                    <td className={`${td} text-right tabular-nums font-semibold`}>
                      {b.estadoBo === 'previsto' && '≈ '}{fmtQty(b.qtyPendiente)}
                      <span className="font-normal text-slate-500"> / {fmtQty(b.qtySolicitada)}</span>
                    </td>
                    <td className={`${td} text-right tabular-nums`}>
                      {dias == null ? '—' : t('cargasSalida.backOrders.dias', { count: dias })}
                    </td>
                    <td className={td}>
                      <span className="flex items-center gap-1.5 flex-wrap">
                        <span
                          title={b.estadoBo === 'previsto' ? t('cargasSalida.backOrders.previstoTooltip') : undefined}
                          className={`text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider whitespace-nowrap ${
                            dark ? CHIP_BO[b.estadoBo].dark : CHIP_BO[b.estadoBo].light
                          }`}
                        >
                          {t(`cargasSalida.backOrders.estado.${b.estadoBo}`)}
                        </span>
                        {b.estadoBo === 'reasignado' && b.cargaDestino && (
                          <span className="font-mono text-[10px]">→ {b.cargaDestino}</span>
                        )}
                        {b.estadoBo === 'cancelado' && b.motivo && (
                          <span className={`text-[10px] italic truncate max-w-[200px] ${dark ? 'text-slate-400' : 'text-slate-600'}`} title={b.motivo}>
                            {b.motivo}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className={`${td} text-right`}>
                      {b.estadoBo === 'abierto' && (
                        <button
                          type="button"
                          onClick={() => { setError(null); setMotivo(''); setACancelar(b) }}
                          className={`text-[11px] font-semibold px-2 py-1 rounded-md border cursor-pointer ${
                            dark ? 'border-white/12 text-rose-300 hover:bg-white/8' : 'border-slate-300 text-rose-600 hover:bg-rose-50'
                          }`}
                        >
                          {t('cargasSalida.backOrders.cancelar')}
                        </button>
                      )}
                    </td>
                  </tr>
                </Fragment>
              )
            })}
          </tbody>
        </table>
      </div>

      {aCancelar && (
        <DialogoModal
          dark={dark}
          titulo={t('cargasSalida.backOrders.cancelarTitulo')}
          confirmarTexto={t('cargasSalida.backOrders.cancelarConfirmar')}
          cancelarTexto={t('cargasSalida.ficha.cancelar')}
          onConfirmar={confirmarCancelacion}
          onCancelar={cerrarDialogo}
          confirmarDeshabilitado={!motivo.trim()}
          ocupado={cancelar.isPending}
          error={error ? msgError(error) : null}
        >
          <p className="mb-2">
            {t('cargasSalida.backOrders.cancelarTexto', {
              articulo: subtituloArticulo(fmtArt(aCancelar.articulo), aCancelar.color, aCancelar.estilo),
              pedido: aCancelar.pedido,
            })}
          </p>
          <label className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold">{t('cargasSalida.backOrders.motivo')}</span>
            <textarea
              value={motivo}
              onChange={e => setMotivo(e.target.value)}
              rows={3}
              required
              className={`text-[12px] rounded-lg border px-2.5 py-1.5 resize-none ${
                dark ? 'bg-slate-950 border-white/10 text-slate-100' : 'bg-white border-slate-300 text-slate-900'
              }`}
            />
          </label>
        </DialogoModal>
      )}
    </div>
  )
}
